import { Decimal } from './money.js';
import { InvalidPricingInputError, PricingImpossibleError } from './errors.js';
import { commissionRateAt } from './commission.js';
import { marginAt } from './margin.js';
import type { PricingContext } from './types.js';

/**
 * §8.6 — Hedef marja göre fiyat çözümü.
 *
 * `profitAt(P)`, P'ye göre monoton artandır (komisyon oranı < 1 olduğu sürece).
 * Bu yüzden çözüm iki aşamalıdır:
 *   5a) Kapalı form → hızlı tahmin
 *   5b) Doğrulama + bisection → kesin sonuç  (ZORUNLU)
 *
 * Kapalı form yalnızca bir tahmindir; kademeli oran ve minimum komisyon rejimleri
 * fonksiyonu parçalı yapar. Bisection her koşulda doğru cevabı verir.
 */

/** §8.6.5a — `K = 1/(1+ts) − r·(1+tc) − te` */
export function computeK(ctx: PricingContext, rate: Decimal): Decimal {
  const taxDivisor = ctx.isTaxRegistered ? new Decimal(1).plus(ctx.salesTaxRate) : new Decimal(1);
  const feeMultiplier = ctx.includeFeeTax ? new Decimal(1).plus(ctx.feeTaxRate) : new Decimal(1);
  return new Decimal(1).div(taxDivisor).minus(rate.mul(feeMultiplier)).minus(ctx.extraTaxRate);
}

/**
 * §8.6.5a — Kapalı form tahmini.
 * `minFeeRegime` true ise minimum komisyon devrede varsayılarak çözülür.
 */
function closedForm(target: Decimal, ctx: PricingContext, rate: Decimal, minFeeRegime: boolean): Decimal | null {
  const taxDivisor = ctx.isTaxRegistered ? new Decimal(1).plus(ctx.salesTaxRate) : new Decimal(1);
  const feeMultiplier = ctx.includeFeeTax ? new Decimal(1).plus(ctx.feeTaxRate) : new Decimal(1);

  if (minFeeRegime) {
    // Minimum komisyon sabit olduğu için P'ye bağlı terim yok:
    //   COST        : P = [C·(1+m) + Fmin·(1+tc)] / (1/(1+ts) − te)
    //   SALE_PRICE  : P = [C + Fmin·(1+tc)] / (1/(1+ts) − te − m)
    const denomBase = new Decimal(1).div(taxDivisor).minus(ctx.extraTaxRate);
    const minFeeWithTax = ctx.minFeeJpy.mul(feeMultiplier);

    if (ctx.marginBasis === 'COST') {
      // `denomBase ≤ 0` demek `K ≤ 0` demektir (K = denomBase − r·(1+tc), r ≥ 0).
      // O durumda çağıran taraftaki geçerlilik kontrolü zaten PricingImpossibleError
      // fırlatmış olur; buraya ulaşılamaz. Yine de sıfıra bölme koruması olarak duruyor.
      /* v8 ignore next */
      if (denomBase.lte(0)) return null;
      return ctx.costJpy.mul(new Decimal(1).plus(target)).plus(minFeeWithTax).div(denomBase);
    }
    const denom = denomBase.minus(target);
    // SALE_PRICE bazında ulaşılabilir en yüksek marj K'dır ve K < denomBase'dir.
    // `denomBase ≤ target` ise `K < target` olur ve geçerlilik kontrolü önce devreye girer.
    /* v8 ignore next */
    if (denom.lte(0)) return null;
    return ctx.costJpy.plus(minFeeWithTax).div(denom);
  }

  const k = computeK(ctx, rate);
  // K ≤ 0 olan bağlamlar `solvePriceForMargin` içindeki geçerlilik kontrolünde
  // PricingImpossibleError ile durdurulur; buraya ulaşılamaz.
  /* v8 ignore next */
  if (k.lte(0)) return null;

  if (ctx.marginBasis === 'COST') {
    return ctx.costJpy.mul(new Decimal(1).plus(target)).div(k);
  }
  const denom = k.minus(target);
  if (denom.lte(0)) return null;
  return ctx.costJpy.div(denom);
}

/**
 * §8.6 — Arama üst sınırı (pazar para biriminin EN KÜÇÜK biriminde).
 *
 * Eskiden 10.000.000'du: JP'de ¥10 milyon (~$66 bin), anlamlı bir sınır. MX
 * panelinde (14 Ekim 2026) en küçük birim centavo ve 1 USD ≈ 1.850 centavo; aynı
 * sınır MX$100.000'e (~$5.400) düşüyor ve pahalı bir ürün "fiyat bulunamadı"ya
 * düşebiliyordu. Sınır 100 kat büyütüldü. JP/UK'de gerçekçi hiçbir fiyat bu
 * aralığın dışında olmadığı için sonuçlar değişmiyor: kapalı form tahmini aynı,
 * ikiye bölme 80 adımda 1e9 / 2^80 ≈ 1e-15 hassasiyetle aynı tam sayıya yuvarlanıyor.
 */
const SEARCH_HI = new Decimal(1_000_000_000);
const SEARCH_LO = new Decimal(1);
/** §8.6.5b — 80 iterasyon → 1 JPY'nin çok altında hassasiyet. */
const BISECTION_STEPS = 80;

/**
 * §8.6 — Hedef marjı sağlayan en küçük tam sayı fiyatı döner.
 *
 * Sonuç **yukarı** yuvarlanır: hedefin altına düşmektense biraz üstünde kalmak yeğdir (§8.6.5b).
 *
 * @throws {PricingImpossibleError} Hiçbir fiyat hedef marjı sağlamıyorsa (§8.6.5c).
 */
export function solvePriceForMargin(target: Decimal, ctx: PricingContext): number {
  if (!target.isFinite()) {
    throw new InvalidPricingInputError('Hedef marj geçerli bir sayı değil.', {
      target: target.toString(),
    });
  }
  if (ctx.costJpy.lt(0)) {
    throw new InvalidPricingInputError('Maliyet negatif olamaz.', { costJpy: ctx.costJpy.toString() });
  }
  if (ctx.marginBasis === 'COST' && ctx.costJpy.lte(0)) {
    throw new InvalidPricingInputError(
      'Maliyet sıfır olduğunda maliyet bazlı marj hedefi anlamsızdır. ' +
        'Alış fiyatı ve kargo ücretinin girildiğini doğrulayın.',
      { costJpy: ctx.costJpy.toString() },
    );
  }

  // ── 5c) Geçerlilik: en yüksek olası fiyatta bile hedefe ulaşılamıyorsa çözüm yok
  const f = (p: Decimal): Decimal => marginAt(p, ctx).minus(target);

  if (f(SEARCH_HI).lt(0)) {
    // Nedeni raporlayabilmek için K'yı da hesapla
    const { rate } = commissionRateAt(SEARCH_HI, ctx);
    const k = computeK(ctx, rate);
    throw new PricingImpossibleError({
      targetMargin: target.toString(),
      costJpy: ctx.costJpy.toString(),
      K: k.toString(),
      reason: k.lte(0)
        ? 'Komisyon ve vergiler satış fiyatının tamamını tüketiyor (K ≤ 0).'
        : 'Hedef marj, ulaşılabilir en yüksek fiyatta bile sağlanamıyor.',
    });
  }

  // ── 5a) Kapalı form tahmini
  //    Hangi rejimde olduğumuzu bilmiyoruz; ikisini de deneyip geçerli olanı alıyoruz.
  const seeds: Decimal[] = [];
  for (const minFeeRegime of [false, true]) {
    // Oran seçimi fiyata bağlı — önce eşiksiz oranla tahmin, sonra doğrula
    const { rate } = commissionRateAt(ctx.tier?.threshold ?? new Decimal(0), ctx);
    const seed = closedForm(target, ctx, rate, minFeeRegime);
    if (seed && seed.isFinite() && seed.gt(0)) seeds.push(seed);
  }

  for (const seed of seeds) {
    const candidate = seed.toDecimalPlaces(0, Decimal.ROUND_CEIL);
    if (candidate.lt(SEARCH_LO) || candidate.gt(SEARCH_HI)) continue;
    // 5b) Doğrulama: tahmin gerçekten hedefi sağlıyor mu ve bir eksiği sağlamıyor mu?
    //     (yani gerçekten EN KÜÇÜK geçerli fiyat mı)
    if (f(candidate).gte(0) && (candidate.lte(SEARCH_LO) || f(candidate.minus(1)).lt(0))) {
      return candidate.toNumber();
    }
  }

  // ── 5b) Bisection — kapalı form tutmadıysa kesin yol
  let lo = SEARCH_LO;
  let hi = SEARCH_HI;

  // Alt sınır zaten hedefi sağlıyorsa en küçük fiyat odur
  if (f(lo).gte(0)) return lo.toNumber();

  for (let i = 0; i < BISECTION_STEPS; i++) {
    const mid = lo.plus(hi).div(2);
    if (f(mid).lt(0)) lo = mid;
    else hi = mid;
  }

  // hi her zaman hedefi SAĞLAYAN taraftır → yukarı yuvarla (§8.6.5b)
  let result = hi.toDecimalPlaces(0, Decimal.ROUND_CEIL);

  // Tam sayıya yuvarlama sonrası hedefin altına düşmediğini garanti et.
  //
  // Bisection üst sınırdan (f ≥ 0 olan bölgeden) daralarak yakınsadığı için `hi`
  // daima bir geçişin hemen üstünde kalır ve `ceil(hi)` de aynı segmentte olur.
  // Tek eşikli komisyon modelinde bu dal tetiklenmez; ikinci bir eşik eklenirse
  // tetiklenebilir — bu yüzden kontrol duruyor.
  /* v8 ignore next */
  if (f(result).lt(0)) result = result.plus(1);
  // ve gereksiz yere bir fazla olmadığını.
  //
  // Bisection sonunda `hi` köke 10⁻¹⁷ mertebesinde yakındır ve `f(hi) ≥ 0` değişmezi
  // korunur; dolayısıyla `ceil(hi)`, `f ≥ 0` sağlayan en küçük tam sayıdır ve döngü
  // gövdesi pratikte hiç çalışmaz. Kademeli oranlar `f`'i parçalı yaptığı için bu
  // güvence gelecekte bozulabilir — o yüzden kontrol duruyor.
  /* v8 ignore next 3 */
  while (result.gt(SEARCH_LO) && f(result.minus(1)).gte(0)) {
    result = result.minus(1);
  }

  return result.toNumber();
}
